import { emptyFragment } from '@execonline-inc/execonline-ui';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { programRoute } from '../../../ClientRoutes';
import { nav } from '../../../Navigation';
import ProgramStore from '../../../ProgramStore';
import {
  ActiveProgramModule,
  Program,
  ProgramModule,
  UpcomingProgramModule,
  whenActive,
  whenUpcoming,
} from '../../../ProgramStore/Types';
import SegmentStore from '../../../SegmentStore';
import { AlreadyTranslated, L, T } from '../../../Translations';
import { whenAccessibleOn } from '../../ProgramOverview/TimeRemaining/Upcoming';

interface Props {
  segmentStore: SegmentStore;
  programStore: ProgramStore;
  programId: Maybe<number>;
  // The module of a segment the learner opened directly. Nothing when they got here
  // with Next, in which case the locked module is the one after the segment they finished.
  openedModuleId: Maybe<number>;
}

// A module in a running program that is gated until a later date.
interface GatedModule {
  kind: 'gated-module';
  module: ActiveProgramModule;
}

// The program itself has not started (the learner has just finished registration, for
// example), so nothing after the current module opens until the program does.
interface ProgramNotStarted {
  kind: 'program-not-started';
  module: UpcomingProgramModule;
  accessDate: Date;
}

// The server refused the segment, but nothing in the program data says why (for
// example, a date gate in a program that is not sequential, where modules are not
// marked as gated). The learner still stays in the course and sees that it is locked.
interface SegmentRefused {
  kind: 'segment-refused';
}

type Locked = GatedModule | ProgramNotStarted | SegmentRefused;

const modulesAfter = <M extends ProgramModule>(
  modules: ReadonlyArray<M>,
  moduleId: number,
): ReadonlyArray<M> => {
  const index = modules.findIndex((m) => m.id === moduleId);
  // A module that is not in the list has no known modules after it.
  return index === -1 ? [] : modules.slice(index + 1);
};

const gatedModule = (program: Program, moduleId: number): Maybe<Locked> =>
  whenActive(program)
    .andThen((p) => fromNullable(modulesAfter(p.modules, moduleId).find((m) => m.isGated)))
    .map((module): Locked => ({ kind: 'gated-module', module }));

const upcomingProgram = (program: Program, moduleId: number): Maybe<Locked> =>
  whenUpcoming(program).andThen((p) =>
    fromNullable(modulesAfter(p.modules, moduleId)[0]).andThen((module) =>
      whenAccessibleOn(p).map(
        (accessDate): Locked => ({
          kind: 'program-not-started',
          module,
          accessDate,
        }),
      ),
    ),
  );

const openedGatedModule = (program: Program, moduleId: number): Maybe<Locked> =>
  whenActive(program)
    .andThen((p) => fromNullable(p.modules.find((m) => m.id === moduleId && m.isGated)))
    .map((module): Locked => ({ kind: 'gated-module', module }));

const openedUpcomingModule = (program: Program, moduleId: number): Maybe<Locked> =>
  whenUpcoming(program).andThen((p) =>
    fromNullable(p.modules.find((m) => m.id === moduleId)).andThen((module) =>
      whenAccessibleOn(p).map(
        (accessDate): Locked => ({ kind: 'program-not-started', module, accessDate }),
      ),
    ),
  );

const refusedSegment = (segmentStore: SegmentStore): Maybe<Locked> =>
  segmentStore.segmentRefused ? just<Locked>({ kind: 'segment-refused' }) : nothing<Locked>();

// What keeps the learner from the module they opened, or from the module after the
// segment they just finished.
const lockedModule = (
  segmentStore: SegmentStore,
  programStore: ProgramStore,
  openedModuleId: Maybe<number>,
): Maybe<Locked> =>
  programStore.programDetailResource
    .map((resource) => resource.payload)
    .andThen((program) =>
      openedModuleId
        .cata({
          Just: (moduleId) =>
            openedGatedModule(program, moduleId).orElse(() =>
              openedUpcomingModule(program, moduleId),
            ),
          Nothing: () =>
            segmentStore.segmentResource
              .map((resource) => resource.payload.moduleId)
              .andThen((moduleId) =>
                gatedModule(program, moduleId).orElse(() => upcomingProgram(program, moduleId)),
              ),
        })
        .orElse(() => refusedSegment(segmentStore)),
    );

const lockedModuleId = (locked: Locked): string => {
  switch (locked.kind) {
    case 'gated-module':
    case 'program-not-started': {
      return String(locked.module.id);
    }
    case 'segment-refused': {
      return 'unknown';
    }
  }
};

const lockedTitle = (locked: Locked): Maybe<React.ReactNode> => {
  switch (locked.kind) {
    case 'gated-module': {
      return just(<AlreadyTranslated content={locked.module.title} />);
    }
    case 'program-not-started': {
      return just(locked.module.title);
    }
    case 'segment-refused': {
      return nothing();
    }
  }
};

const lockedMessage = (locked: Locked): React.ReactNode => {
  switch (locked.kind) {
    case 'gated-module': {
      return locked.module.gatedMessage
        .map((message) => <AlreadyTranslated content={message} />)
        .getOrElseValue(<></>);
    }
    case 'program-not-started': {
      return (
        <T
          kind="This program will be accessible on <date/>. Return then to take this course."
          date={<L localizeable={locked.accessDate} format="long-month-day-year" />}
        />
      );
    }
    case 'segment-refused': {
      return <T kind="You do not have access to this segment yet" />;
    }
  }
};

/**
 * Shown in place of the segment content when the learner has finished every segment
 * open to them, or opened a segment that is not open yet. If the module is gated, or
 * the program has not started yet, the learner stays in the course (with the course
 * tools) and sees when it opens. If the server refused the segment for another reason,
 * the learner stays in the course and sees that it is locked. If nothing is locked and
 * nothing was refused (the course is finished), the learner goes to the program page.
 */
function NextModuleLocked({ segmentStore, programStore, programId, openedModuleId }: Props) {
  const locked = lockedModule(segmentStore, programStore, openedModuleId);
  const programLoaded = programStore.programDetailResource.isJust();
  const nothingLocked = locked.isNothing();
  const programIdValue = programId.getOrElseValue(0);

  // Nothing is locked and nothing was refused: the course is finished. Go to the
  // program page as before.
  React.useEffect(() => {
    if (programLoaded && nothingLocked && programIdValue > 0) {
      nav(programRoute(programIdValue));
    }
  }, [programLoaded, nothingLocked, programIdValue]);

  return locked
    .map((locked) => (
      <div
        className="flex min-h-[300px] flex-col items-center justify-center gap-3 px-6 py-16 text-center"
        role="status"
        data-test-next-module-locked={lockedModuleId(locked)}
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          fill="none"
          className="stroke-gray-500"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        <span className="text-sm font-semibold uppercase tracking-wider text-gray-500">
          <T kind="Locked" />
        </span>
        {lockedTitle(locked)
          .map((title) => <h2 className="m-0 text-2xl font-semibold">{title}</h2>)
          .getOrElse(emptyFragment)}
        <div className="max-w-xl text-base">{lockedMessage(locked)}</div>
      </div>
    ))
    .getOrElse(emptyFragment);
}

export default observer(NextModuleLocked);
